import React, { useState } from "react";
import {
  Card,
  Upload,
  Button,
  Spin,
  Alert,
  Descriptions,
  Tag,
  Typography,
  Space,
  Divider,
} from "@pankod/refine-antd";
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import axios from "axios";
import { getJwtLocalStorage } from "helpers/local-storage";
import { WATERMARK_API_URL } from "constants/index";

const { Title, Text } = Typography;

interface WatermarkResult {
  detected: boolean;
  payload?: string;
  inDatabase?: boolean;
  user?: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  track?: {
    trackId: string;
    title: string;
    artist: string;
    price: number;
  };
  watermark?: {
    watermarkId: string;
    downloadedAt: string;
  };
  forensics?: {
    daysSinceDownload: number;
    analysisDate: string;
  };
  message?: string;
}

export const WatermarkVerify: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WatermarkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    if (fileList.length === 0) {
      setError("Please select a file to verify");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", fileList[0].originFileObj as Blob);

      // Get token from local storage
      const JWT = getJwtLocalStorage();

      // Call cc-watermark service directly
      const response = await axios.post(
        `${WATERMARK_API_URL}/watermark/verify?includeForensics=true`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: JWT ? `Bearer ${JWT}` : "",
          },
        }
      );

      setResult(response.data);
    } catch (err: any) {
      console.error("Verification error:", err);
      // Detailed error handling
      const errorMessage = err.response?.data?.message
        ? Array.isArray(err.response.data.message)
          ? err.response.data.message.join(", ")
          : err.response.data.message
        : err.message || "Failed to verify watermark";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={3}>Watermark Verification Tool</Title>
        <Text type="secondary">
          Upload an audio or video file to verify if it contains a watermark and
          retrieve forensic information about the download.
        </Text>

        <Divider />

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Upload
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            beforeUpload={() => false}
            maxCount={1}
            accept="audio/*,video/*,.mp3,.wav,.flac,.m4a,.aac,.ogg,.mp4,.mov,.mkv,.webm"
          >
            <Button icon={<UploadOutlined />}>Select Media File</Button>
          </Upload>

          <Button
            type="primary"
            onClick={handleVerify}
            loading={loading}
            disabled={fileList.length === 0}
            size="large"
          >
            Verify Watermark
          </Button>

          {loading && (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="large" />
              <div style={{ marginTop: "16px" }}>
                <Text>Analyzing media file...</Text>
              </div>
            </div>
          )}

          {error && (
            <Alert message="Error" description={error} type="error" showIcon />
          )}

          {result && !loading && (
            <Card
              style={{
                backgroundColor: result.detected ? "#f6ffed" : "#fff7e6",
                borderColor: result.detected ? "#b7eb8f" : "#ffd591",
              }}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <div>
                  <Text strong style={{ fontSize: "16px", marginRight: "8px" }}>
                    Detection Status:
                  </Text>
                  {result.detected ? (
                    <Tag
                      icon={<CheckCircleOutlined />}
                      color="success"
                      style={{ fontSize: "14px" }}
                    >
                      Watermark Detected
                    </Tag>
                  ) : (
                    <Tag
                      icon={<CloseCircleOutlined />}
                      color="warning"
                      style={{ fontSize: "14px" }}
                    >
                      No Watermark
                    </Tag>
                  )}
                </div>

                {result.message && (
                  <Alert
                    message={result.message}
                    type={result.inDatabase ? "info" : "warning"}
                    showIcon
                  />
                )}

                {result.detected && result.payload && (
                  <>
                    <Divider />
                    <Descriptions
                      title="Watermark Information"
                      bordered
                      column={1}
                    >
                      <Descriptions.Item label="Payload Hash">
                        <Text code copyable>
                          {result.payload}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Database Status">
                        <Tag color={result.inDatabase ? "green" : "orange"}>
                          {result.inDatabase
                            ? "Found in Database"
                            : "Not in Database"}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {result.user && (
                  <>
                    <Divider />
                    <Descriptions title="User Information" bordered column={1}>
                      <Descriptions.Item label="User ID">
                        <Text code>{result.user.userId}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        <Text copyable>{result.user.email}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Full Name">
                        {result.user.firstName} {result.user.lastName}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {result.track && (
                  <>
                    <Divider />
                    <Descriptions title="Track Information" bordered column={1}>
                      <Descriptions.Item label="Track ID">
                        <Text code>{result.track.trackId}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Title">
                        <Text strong>{result.track.title}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Artist">
                        {result.track.artist}
                      </Descriptions.Item>
                      <Descriptions.Item label="Price">
                        ${result.track.price}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {result.watermark && (
                  <>
                    <Divider />
                    <Descriptions title="Download Record" bordered column={1}>
                      <Descriptions.Item label="Watermark ID">
                        <Text code>{result.watermark.watermarkId}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Downloaded At">
                        {formatDate(result.watermark.downloadedAt)}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {result.forensics && (
                  <>
                    <Divider />
                    <Descriptions title="Forensic Analysis" bordered column={1}>
                      <Descriptions.Item label="Days Since Download">
                        <Tag color="blue">
                          {result.forensics.daysSinceDownload} days ago
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Analysis Date">
                        {formatDate(result.forensics.analysisDate)}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}
              </Space>
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
};
